import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useRBAC } from '../../../contexts/RBACContext';
import { permissionAPI, roleAPI } from '../../../services/rbacApi';

const getSubModule = (code) => {
  if (!code) return '';
  const parts = code.split(':');
  return parts.length >= 2 ? parts[1] : '';
};

const matchesPermissionFilters = (permission, { search = '', module = '', subModule = '' }) => {
  if (module && permission.module !== module) return false;
  if (subModule && getSubModule(permission.code) !== subModule) return false;
  if (!search) return true;

  const searchLower = search.toLowerCase();
  return (
    permission.name?.toLowerCase().includes(searchLower) ||
    permission.code?.toLowerCase().includes(searchLower) ||
    permission.description?.toLowerCase().includes(searchLower) ||
    permission.module?.toLowerCase().includes(searchLower) ||
    getSubModule(permission.code).toLowerCase().includes(searchLower)
  );
};

const RolePermissions = ({ role, onBack }) => {
  const {
    loading,
    bulkAssignPermissions,
    bulkRevokePermissions,
  } = useRBAC();

  const listScrollRef = useRef(null);
  const bulkInFlightRef = useRef(false);
  const [roleDetails, setRoleDetails] = useState(role);
  const [fullCatalog, setFullCatalog] = useState([]);
  const [cachedModules, setCachedModules] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [operation, setOperation] = useState('assign');
  const [filterModule, setFilterModule] = useState('');
  const [filterSubModule, setFilterSubModule] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  const defaultFilters = {
    search: '',
    module: '',
    subModule: '',
  };

  const assignedPermissions = roleDetails?.permissions || [];
  const assignedCodeSet = useMemo(
    () => new Set(assignedPermissions.map((p) => p.code)),
    [assignedPermissions]
  );

  const totalActiveCount = fullCatalog.length;
  const assignedCount = assignedPermissions.length;
  const availableCount = Math.max(totalActiveCount - assignedCount, 0);

  const activeFilters = useMemo(
    () => ({
      search: searchTerm.trim(),
      module: filterModule,
      subModule: filterSubModule,
    }),
    [searchTerm, filterModule, filterSubModule]
  );

  const refreshRoleDetails = useCallback(async (roleCode, catalog = fullCatalog) => {
    const response = await roleAPI.getRoleForAssignment(roleCode);
    const freshRole = response.data.data.role || {};
    const assignedCodes = response.data.data.assignedCodes
      || freshRole.assignedCodes
      || [];
    const assignedCodeLookup = new Set(assignedCodes);
    const hydratedPermissions = (catalog || [])
      .filter((p) => assignedCodeLookup.has(p.code));

    const nextRole = {
      ...freshRole,
      assignedCodes,
      permissions: hydratedPermissions,
      permissionCount: assignedCodes.length,
    };
    setRoleDetails(nextRole);
    return nextRole;
  }, [fullCatalog]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      setInitialLoading(true);
      // Show list-card role data immediately while fresh assignment data loads
      if (role) {
        setRoleDetails(role);
      }

      try {
        const roleCode = role?.code;
        if (!roleCode) return;

        // Catalog is cached + single-flight; modules derived from it.
        // Role assignment view returns codes only; hydrate from catalog.
        const [permissions, roleResponse] = await Promise.all([
          permissionAPI.fetchAllPermissions({ isActive: true }),
          roleAPI.getRoleForAssignment(roleCode),
        ]);
        if (cancelled) return;

        setFullCatalog(permissions);
        setCachedModules(
          [...new Set(permissions.map((p) => p.module).filter(Boolean))].sort()
        );

        const freshRole = roleResponse.data?.data?.role || {};
        const assignedCodes = roleResponse.data?.data?.assignedCodes
          || freshRole.assignedCodes
          || [];
        const assignedCodeLookup = new Set(assignedCodes);
        setRoleDetails({
          ...freshRole,
          assignedCodes,
          permissions: permissions.filter((p) => assignedCodeLookup.has(p.code)),
          permissionCount: assignedCodes.length,
        });
      } catch (error) {
        console.error('Failed to initialize role permissions:', error);
        if (!cancelled && role) {
          setRoleDetails(role);
        }
      } finally {
        if (!cancelled) {
          setInitialLoading(false);
        }
      }
    };

    initialize();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role?.code]);

  // Keep assigned permission objects in sync when catalog arrives/changes after role codes
  useEffect(() => {
    if (!roleDetails?.assignedCodes?.length || fullCatalog.length === 0) return;
    const lookup = new Set(roleDetails.assignedCodes);
    const hydrated = fullCatalog.filter((p) => lookup.has(p.code));
    if (hydrated.length !== (roleDetails.permissions || []).length) {
      setRoleDetails((prev) => ({
        ...prev,
        permissions: hydrated,
        permissionCount: lookup.size,
      }));
    }
  }, [fullCatalog, roleDetails?.assignedCodes]);

  useEffect(() => {
    setFilterSubModule('');
    setSelectedPermissions([]);
  }, [filterModule, operation]);

  useEffect(() => {
    setSelectedPermissions([]);
  }, [searchTerm, filterSubModule]);

  useEffect(() => {
    if (listScrollRef.current) {
      listScrollRef.current.scrollTop = 0;
    }
  }, [operation, searchTerm, filterModule, filterSubModule]);

  const filteredPermissions = useMemo(() => {
    const source = operation === 'assign'
      ? fullCatalog.filter((p) => !assignedCodeSet.has(p.code))
      : assignedPermissions;

    return source.filter((p) => matchesPermissionFilters(p, activeFilters));
  }, [operation, fullCatalog, assignedPermissions, assignedCodeSet, activeFilters]);

  const modules = cachedModules.length > 0
    ? cachedModules
    : [...new Set(fullCatalog.map((p) => p.module).filter(Boolean))].sort();

  const subModules = useMemo(() => {
    const source = operation === 'assign'
      ? fullCatalog.filter((p) => !assignedCodeSet.has(p.code))
      : assignedPermissions;
    const scopedPerms = filterModule
      ? source.filter((p) => p.module === filterModule)
      : source;
    return [...new Set(scopedPerms.map((p) => getSubModule(p.code)).filter(Boolean))].sort();
  }, [operation, fullCatalog, assignedPermissions, assignedCodeSet, filterModule]);

  const hasModifiedFilters = () =>
    searchTerm !== defaultFilters.search ||
    filterModule !== defaultFilters.module ||
    filterSubModule !== defaultFilters.subModule;

  const handleResetFilters = () => {
    setSearchTerm('');
    setFilterModule('');
    setFilterSubModule('');
  };

  const handlePermissionToggle = (permission) => {
    setSelectedPermissions((prev) => {
      const isSelected = prev.find((p) => p.code === permission.code);
      if (isSelected) {
        return prev.filter((p) => p.code !== permission.code);
      }
      return [...prev, permission];
    });
  };

  const handleBulkOperation = async () => {
    if (selectedPermissions.length === 0 || !roleDetails?.code) return;
    if (bulkInFlightRef.current) return;
    bulkInFlightRef.current = true;

    const permissionCodes = selectedPermissions.map((p) => p.code);

    try {
      const result = operation === 'assign'
        ? await bulkAssignPermissions(roleDetails.code, permissionCodes)
        : await bulkRevokePermissions(roleDetails.code, permissionCodes, 'Bulk revoke operation');

      if (result.success) {
        const updatedRole = result.data?.role;
        if (Array.isArray(updatedRole?.assignedCodes)) {
          // The response already carries the new assignment; no second round trip on slow links.
          const assignedCodes = updatedRole.assignedCodes;
          const lookup = new Set(assignedCodes);
          setRoleDetails((prev) => ({
            ...prev,
            assignedCodes,
            permissions: fullCatalog.filter((p) => lookup.has(p.code)),
            permissionCount: assignedCodes.length,
          }));
        } else {
          await refreshRoleDetails(roleDetails.code, fullCatalog);
        }
        setSelectedPermissions([]);
      }
    } catch (error) {
      console.error('Bulk operation failed:', error);
    } finally {
      bulkInFlightRef.current = false;
    }
  };

  const displayRole = roleDetails || role;

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 h-full w-full z-50 flex items-center justify-center p-2">
      <div className="relative w-full max-w-7xl bg-white rounded-lg shadow-2xl border border-gray-100 flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Role Details & Permissions - {displayRole?.name}
                </h3>
                {!initialLoading && totalActiveCount > 0 && (
                  <p className="text-xs text-indigo-100">
                    {totalActiveCount} active permissions in system
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onBack}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {initialLoading ? (
          <>
            <div className="overflow-y-auto p-4 max-h-[calc(100vh-10rem)]">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4 space-y-4">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4 sticky top-0">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Role Details</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Role Name</label>
                        <p className="text-sm font-medium text-gray-900 mt-1">{displayRole?.name}</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Role Code</label>
                        <p className="text-sm font-mono text-gray-900 mt-1 bg-white px-2 py-1 rounded border border-gray-200 inline-block">{displayRole?.code}</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</label>
                        <p className="text-sm text-gray-700 mt-1">{displayRole?.description || 'No description provided'}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-300">
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Level</label>
                          <div className="mt-1">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Level {displayRole?.level}
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</label>
                          <div className="mt-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              displayRole?.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {displayRole?.isActive !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-8 space-y-4">
                  <div className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                  <div className="flex gap-2">
                    <div className="flex-1 h-10 bg-gray-100 rounded-md animate-pulse" />
                    <div className="w-44 h-10 bg-gray-100 rounded-md animate-pulse" />
                  </div>
                  <div className="h-12 bg-gray-50 rounded-lg border border-gray-200 animate-pulse" />
                  <div className="h-96 flex items-center justify-center border border-gray-200 rounded-lg bg-gray-50">
                    <div className="text-center">
                      <svg className="animate-spin h-8 w-8 text-indigo-600 mx-auto" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-500">Loading permissions...</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-gray-100">
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
              >
                Close
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="overflow-y-auto p-4 max-h-[calc(100vh-10rem)]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-4 space-y-4">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4 sticky top-0">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Role Details</h4>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Role Name</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{roleDetails?.name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Role Code</label>
                    <p className="text-sm font-mono text-gray-900 mt-1 bg-white px-2 py-1 rounded border border-gray-200 inline-block">{roleDetails?.code}</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</label>
                    <p className="text-sm text-gray-700 mt-1">{roleDetails?.description || 'No description provided'}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-300">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Level</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          roleDetails?.level === 1 ? 'bg-red-100 text-red-800' :
                          roleDetails?.level === 2 ? 'bg-orange-100 text-orange-800' :
                          roleDetails?.level === 3 ? 'bg-yellow-100 text-yellow-800' :
                          roleDetails?.level === 4 ? 'bg-green-100 text-green-800' :
                          roleDetails?.level === 5 ? 'bg-blue-100 text-blue-800' :
                          roleDetails?.level === 6 ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          Level {roleDetails?.level}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          roleDetails?.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {roleDetails?.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Permissions</label>
                      <div className="flex items-center mt-1">
                        <span className="text-sm font-semibold text-gray-900">{assignedCount}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Users</label>
                      <div className="flex items-center mt-1">
                        <span className="text-sm font-semibold text-gray-900">{roleDetails?.userCount || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 space-y-4">
              <div>
                <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setOperation('assign');
                      setSelectedPermissions([]);
                    }}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      operation === 'assign'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Assign Permissions ({availableCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setOperation('revoke');
                      setSelectedPermissions([]);
                    }}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      operation === 'revoke'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Revoke Permissions ({assignedCount})
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <div className="flex-1 w-full">
                  <input
                    type="text"
                    placeholder="Search by name, code, module, or resource..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div className="w-full sm:w-44">
                  <select
                    value={filterModule}
                    onChange={(e) => setFilterModule(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Modules</option>
                    {modules.map((module) => (
                      <option key={module} value={module}>{module}</option>
                    ))}
                  </select>
                </div>
                {filterModule && subModules.length > 0 && (
                  <div className="w-full sm:w-48">
                    <select
                      value={filterSubModule}
                      onChange={(e) => setFilterSubModule(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">All Resources</option>
                      {subModules.map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                  </div>
                )}

                {hasModifiedFilters() && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                    title="Reset filters"
                  >
                    Reset
                  </button>
                )}
              </div>

              <div className="flex flex-wrap justify-between items-center gap-2 bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPermissions.length === filteredPermissions.length && filteredPermissions.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPermissions(filteredPermissions);
                        } else {
                          setSelectedPermissions([]);
                        }
                      }}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      Select all matching ({filteredPermissions.length})
                    </span>
                  </label>
                  {selectedPermissions.length > 0 && (
                    <span className="text-sm text-indigo-700">
                      {selectedPermissions.length} selected
                    </span>
                  )}
                </div>
                {selectedPermissions.length > 0 && (
                  <button
                    type="button"
                    onClick={handleBulkOperation}
                    disabled={loading.assignment}
                    className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                      operation === 'assign'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    } disabled:opacity-50`}
                  >
                    {loading.assignment ? 'Processing...' : (
                      operation === 'assign' ? 'Assign Selected' : 'Revoke Selected'
                    )}
                  </button>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                {filteredPermissions.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p>No permissions match your filters</p>
                    {hasModifiedFilters() ? (
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        Clear filters
                      </button>
                    ) : operation === 'assign' ? (
                      <p className="text-sm mt-1">All permissions are already assigned to this role</p>
                    ) : (
                      <p className="text-sm mt-1">This role has no permissions to revoke</p>
                    )}
                  </div>
                ) : (
                  <>
                    <div ref={listScrollRef} className="max-h-96 overflow-y-auto">
                      <div
                        key={`${operation}-${filterModule}-${filterSubModule}-${searchTerm}-${filteredPermissions.length}`}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-0"
                      >
                        {filteredPermissions.map((permission, index) => {
                          const isSelected = selectedPermissions.find((p) => p.code === permission.code);
                          const isLeftColumn = index % 2 === 0;
                          const isLastRow = index >= filteredPermissions.length - 2;
                          return (
                            <div
                              key={permission.code}
                              className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                                isSelected ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''
                              } ${isLeftColumn ? 'lg:border-r border-gray-200' : ''} ${
                                !isLastRow ? 'border-b border-gray-200' : ''
                              }`}
                              onClick={() => handlePermissionToggle(permission)}
                            >
                              <div className="flex items-start">
                                <input
                                  type="checkbox"
                                  checked={!!isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handlePermissionToggle(permission);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 mt-0.5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded flex-shrink-0"
                                />
                                <div className="ml-3 flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <h4 className="text-sm font-medium text-gray-900 break-words">
                                      {permission.name}
                                    </h4>
                                    <span className="text-xs text-gray-500 font-mono whitespace-nowrap">
                                      {permission.code}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                    {permission.description}
                                  </p>
                                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      {permission.module}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                      permission.level <= 2 ? 'bg-green-100 text-green-800' :
                                      permission.level <= 4 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}>
                                      Level {permission.level}
                                    </span>
                                    {permission.category && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {permission.category}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-gray-50 border-t border-gray-200 px-3 py-2">
                      <span className="text-xs text-gray-500">
                        {filteredPermissions.length} permission{filteredPermissions.length === 1 ? '' : 's'}
                        {hasModifiedFilters() ? ' matching filters' : ''} — scroll to browse
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
          >
            Close
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default RolePermissions;
